/**
 * `IdentityService` —— 创建 / 导入 / 解锁 / 锁定 / 改口令 / 注册 DID / 资料 / 导出身份包。
 *
 * 修复原型缺陷 **B1/B2/B3/B5**（ARCH §1.4）：
 *  - B1 助记词 → HMAC-SHA512 分域派生 → 三套种子，确定性 + 持久化
 *  - B2 私钥永不落 localStorage，整包密钥 seal 进 IndexedDB `keystore`
 *  - B3 `address = 0x + sha256(signPub)[:40]`，`did = did:dpb:0x…`
 *  - B5 口令 → PBKDF2(310k) → AES-256-GCM；锁定 = 内存密钥 `fill(0)`
 *
 * **所有方法只抛 `MeshLinkError`**（ARCH §8.2）。
 */

import { deriveKeysFromSeed, rebuildKeys, type DerivedKeys } from '@/crypto/derivation';
import { generateMnemonic, mnemonicToSeed, validateMnemonic } from '@/crypto/mnemonic';
import {
  exportIdentityBackup,
  openKeystore,
  reencryptKeystore,
  sealKeystore,
  type EncryptedKeystore,
  type KeyBundle,
} from '@/crypto/keystore';
import { ed25519Sign } from '@/crypto/noble';
import { canonicalBytes } from '@/crypto/canonical';
import { base64ToBytes, bytesToBase64 } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import type { IStorage } from '@/storage/IStorage';
import type { IChainClient, Micro } from '@shared/chain';
import type { IdentityMeta } from '@shared/types';
import { LS_KEYS } from '@/config';

const logger = createLogger('service:identity');

/** 内存中驻留的解锁态密钥（锁定后即清零）。 */
interface MemoryKeys {
  keys: DerivedKeys;
  bundle: KeyBundle;
}

export interface RegisterDidResult {
  registered: boolean;
  txHash?: string;
  faucetTxHash?: string;
  balanceMicro?: Micro;
}

export class IdentityService {
  private mem: MemoryKeys | null = null;
  private meta: IdentityMeta | null = null;
  private openedDid: string | null = null;

  constructor(
    private readonly storage: IStorage,
    private chain: IChainClient,
  ) {}

  /** 替换链客户端（容器切换实现时调用）。 */
  setChainClient(chain: IChainClient): void {
    this.chain = chain;
  }

  // ─────────────── 状态查询 ───────────────

  get isUnlocked(): boolean {
    return this.mem !== null;
  }

  get current(): IdentityMeta | null {
    return this.meta;
  }

  get did(): string {
    return this.mem?.keys.did ?? this.meta?.did ?? '';
  }

  get address(): string {
    return (this.mem?.keys.address ?? this.meta?.address ?? '') as string;
  }

  get peerId(): string {
    return this.mem?.keys.peerId ?? this.meta?.peerId ?? '';
  }

  get signPubB64(): string {
    this.requireUnlocked();
    return bytesToBase64(this.mem!.keys.signPub);
  }

  get encPubB64(): string {
    this.requireUnlocked();
    return bytesToBase64(this.mem!.keys.encPub);
  }

  /** 取内存密钥；未解锁抛 `E_LOCKED`。 */
  requireUnlocked(): DerivedKeys {
    if (!this.mem) throw new MeshLinkError('E_LOCKED');
    return this.mem.keys;
  }

  /** 对任意字节签名（链上操作 / Manifest 签名共用）。 */
  sign(bytes: Uint8Array): Uint8Array {
    return ed25519Sign(bytes, this.requireUnlocked().signSeed);
  }

  /** 对任意对象签名（走 canonical 序列化）。 */
  signObject(value: unknown): string {
    return bytesToBase64(this.sign(canonicalBytes(value)));
  }

  /** 会话加密用的本端私钥种子（复制一份，避免外部持有后被清零影响）。 */
  sessionSeed(): { encSeed: Uint8Array; encPub: Uint8Array; signSeed: Uint8Array } {
    const keys = this.requireUnlocked();
    return {
      encSeed: new Uint8Array(keys.encSeed),
      encPub: new Uint8Array(keys.encPub),
      signSeed: new Uint8Array(keys.signSeed),
    };
  }

  /**
   * 网络层节点私钥种子（libp2p 节点身份，与 DID 同源确定性派生）。
   * 仅传给 `net/*`，**禁止落盘、禁止进 store**。
   */
  netSeed(): Uint8Array {
    return new Uint8Array(this.requireUnlocked().peerSeed);
  }

  // ─────────────── 身份生命周期 ───────────────

  /**
   * 生成 12 词助记词（全程无网络请求）。
   * 助记词**只返回给调用方内存**，服务内部不留存。
   */
  createIdentity(): { words: string[] } {
    const words = generateMnemonic(128);
    logger.info('已生成新助记词（仅内存）');
    return { words };
  }

  /**
   * 校验助记词 + 设口令 + seal keystore + 落库 + 注册 DID（best-effort）。
   * @param words 12 词
   * @param password 口令
   * @param name 昵称（可选）
   */
  async verifyAndSeal(words: string[], password: string, name = ''): Promise<IdentityMeta> {
    const identity = await this.deriveAndSeal(words, password, name);
    // 注册与水龙头失败不阻断身份创建（链服务可能未启动）
    await this.bootstrapChain();
    const refreshed = await this.reloadMeta();
    return refreshed ?? identity;
  }

  /** 导入助记词恢复身份（与创建共用同一派生口径，保证 DID 一致）。 */
  async importMnemonic(words: string[], password: string, name = ''): Promise<IdentityMeta> {
    return this.deriveAndSeal(words, password, name);
  }

  private async deriveAndSeal(words: string[], password: string, name: string): Promise<IdentityMeta> {
    const list = words.map((w) => w.trim().toLowerCase()).filter(Boolean);
    if (!validateMnemonic(list)) throw new MeshLinkError('E_MNEMONIC_INVALID');
    if (!password || password.length < 6) throw new MeshLinkError('E_INVALID_INPUT', '口令至少 6 位');

    const seed = await mnemonicToSeed(list);
    const keys = deriveKeysFromSeed(seed);
    seed.fill(0);

    const bundle: KeyBundle = {
      signSeed: bytesToBase64(keys.signSeed),
      encSeed: bytesToBase64(keys.encSeed),
      peerSeed: bytesToBase64(keys.peerSeed),
      mnemonic: list,
    };

    const keystore = await sealKeystore(bundle, password, {
      did: keys.did,
      address: keys.address,
      peerId: keys.peerId,
    });

    // 分库：库名随 DID
    await this.storage.open(keys.did);
    await this.storage.putKeystore(keystore);

    const meta: IdentityMeta = {
      did: keys.did,
      address: keys.address,
      peerId: keys.peerId,
      name: name.trim() || `用户-${keys.address.slice(2, 8)}`,
      registered: false,
      faucetClaimed: false,
      anchorUsed: 0,
      createdAt: Date.now(),
    };
    await this.storage.putIdentityMeta(meta);

    this.mem = { keys, bundle };
    this.meta = meta;
    this.openedDid = keys.did;
    try {
      localStorage.setItem(LS_KEYS.lastDid, keys.did);
    } catch {
      // 隐私模式下 localStorage 可能不可写，忽略
    }
    logger.info('身份已创建并 seal', { did: keys.did.slice(0, 24) });
    return meta;
  }

  /** 用已存在的 keystore 解锁。 */
  async unlock(password: string): Promise<IdentityMeta> {
    const did = await this.resolveDid();
    if (!did) throw new MeshLinkError('E_IDENTITY_NOT_FOUND');
    if (this.openedDid !== did) {
      await this.storage.open(did);
      this.openedDid = did;
    }

    const keystore = await this.storage.getKeystore();
    if (!keystore) throw new MeshLinkError('E_IDENTITY_NOT_FOUND');

    let bundle: KeyBundle;
    try {
      bundle = await openKeystore(keystore, password);
    } catch (err) {
      if (err instanceof MeshLinkError) throw err;
      throw new MeshLinkError('E_PASSWORD_INVALID', undefined, err);
    }

    const keys = rebuildKeys(
      base64ToBytes(bundle.signSeed),
      base64ToBytes(bundle.encSeed),
      base64ToBytes(bundle.peerSeed),
    );
    if (keys.did !== keystore.did) throw new MeshLinkError('E_PASSWORD_INVALID', 'keystore 与身份不匹配');

    this.mem = { keys, bundle };
    this.meta = (await this.storage.getIdentityMeta()) ?? {
      did: keys.did,
      address: keys.address,
      peerId: keys.peerId,
      name: `用户-${keys.address.slice(2, 8)}`,
      registered: false,
      faucetClaimed: false,
      anchorUsed: 0,
      createdAt: keystore.createdAt,
    };
    logger.info('身份已解锁', { did: keys.did.slice(0, 24) });
    return this.meta;
  }

  /** 锁定：内存密钥清零 + 清空内存态。 */
  lock(): void {
    if (this.mem) {
      this.mem.keys.signSeed.fill(0);
      this.mem.keys.encSeed.fill(0);
      this.mem.keys.peerSeed.fill(0);
      this.mem = null;
    }
    this.meta = null;
    logger.info('身份已锁定');
  }

  /** 本机是否已有身份（无需解锁即可判断）。 */
  async hasIdentity(): Promise<boolean> {
    return (await this.resolveDid()) !== null;
  }

  /** 读取非敏感身份元信息（锁屏页展示）。 */
  async loadMeta(): Promise<IdentityMeta | null> {
    const did = await this.resolveDid();
    if (!did) return null;
    if (this.openedDid !== did) {
      await this.storage.open(did);
      this.openedDid = did;
    }
    const meta = await this.storage.getIdentityMeta();
    this.meta = meta;
    return meta;
  }

  private async reloadMeta(): Promise<IdentityMeta | null> {
    const meta = await this.storage.getIdentityMeta();
    if (meta) this.meta = meta;
    return meta;
  }

  /**
   * 定位本机身份 DID：优先 IndexedDB（按 localStorage 白名单里的 `lastDid` 找库），
   * 无则回落到「默认库」探测。**私钥永不进 localStorage**，这里只存 DID（非敏感）。
   */
  private async resolveDid(): Promise<string | null> {
    if (this.meta?.did) return this.meta.did;
    if (this.mem?.keys.did) return this.mem.keys.did;

    let lastDid: string | null = null;
    try {
      lastDid = localStorage.getItem(LS_KEYS.lastDid);
    } catch {
      lastDid = null;
    }
    if (lastDid) {
      await this.storage.open(lastDid);
      this.openedDid = lastDid;
      const meta = await this.storage.getIdentityMeta();
      if (meta?.did) {
        this.meta = meta;
        return meta.did;
      }
    }
    return lastDid;
  }

  // ─────────────── 链上注册 ───────────────

  /**
   * 注册 DID + 领水龙头（**显式调用**，会抛 `E_CHAIN_UNREACHABLE`）。
   * 链上操作禁止自动签名，UI 必须先走 `ConfirmTxDialog`（PRD §8.3 S7）。
   */
  async registerDid(): Promise<RegisterDidResult> {
    const keys = this.requireUnlocked();
    const address = keys.address as `0x${string}`;
    try {
      const res = await this.chain.registerAccount(address, bytesToBase64(keys.signPub));
      await this.patchMeta({ registered: true });
      return { registered: true, txHash: res.txHash };
    } catch (err) {
      if (err instanceof MeshLinkError) throw err;
      throw new MeshLinkError('E_CHAIN_UNREACHABLE', undefined, err);
    }
  }

  /** 领取新手奖励（100 DPB，地址级去重）。 */
  async claimFaucet(): Promise<{ txHash: string; balanceMicro: Micro }> {
    const keys = this.requireUnlocked();
    const address = keys.address as `0x${string}`;
    const res = await this.chain.faucet(address);
    await this.patchMeta({ faucetClaimed: true });
    const balanceMicro = await this.chain.getBalance(address);
    return { txHash: res.txHash, balanceMicro };
  }

  /** 创建后的 best-effort 链引导（失败只记日志，不阻断）。 */
  private async bootstrapChain(): Promise<void> {
    try {
      const res = await this.registerDid();
      if (res.registered) {
        try {
          await this.claimFaucet();
        } catch (err) {
          logger.warn('水龙头领取失败（可稍后重试）', { err });
        }
      }
    } catch (err) {
      logger.warn('DID 注册失败（可稍后重试）', { err });
    }
  }

  /** 查询余额（不抛链不可达以外的错误）。 */
  async getBalanceMicro(): Promise<Micro> {
    const address = this.requireUnlocked().address as `0x${string}`;
    return this.chain.getBalance(address);
  }

  // ─────────────── 资料 / 口令 ───────────────

  /** 更新资料（昵称 / 头像 / 简介），只改 IndexedDB 的元信息。 */
  async updateProfile(patch: Partial<Pick<IdentityMeta, 'name' | 'avatarCid' | 'bio'>>): Promise<IdentityMeta> {
    const current = (await this.reloadMeta()) ?? this.meta;
    if (!current) throw new MeshLinkError('E_IDENTITY_NOT_FOUND');
    const next: IdentityMeta = { ...current, ...patch };
    await this.storage.putIdentityMeta(next);
    this.meta = next;
    return next;
  }

  private async patchMeta(patch: Partial<IdentityMeta>): Promise<void> {
    const current = (await this.reloadMeta()) ?? this.meta;
    if (!current) return;
    const next: IdentityMeta = { ...current, ...patch };
    await this.storage.putIdentityMeta(next);
    this.meta = next;
  }

  /** 改口令：解开旧 keystore → 用新口令重新 seal。 */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    if (!newPassword || newPassword.length < 6) throw new MeshLinkError('E_INVALID_INPUT', '新口令至少 6 位');
    const keystore = await this.storage.getKeystore();
    if (!keystore) throw new MeshLinkError('E_IDENTITY_NOT_FOUND');
    const next = await reencryptKeystore(keystore, oldPassword, newPassword);
    await this.storage.putKeystore(next);
    logger.info('口令已更新');
  }

  /** 导出加密身份包（二次口令确认后调用）。 */
  async exportEncrypted(password: string): Promise<string> {
    const keystore = await this.storage.getKeystore();
    if (!keystore) throw new MeshLinkError('E_IDENTITY_NOT_FOUND');
    await openKeystore(keystore, password); // 口令错误直接抛 E_PASSWORD_INVALID
    return exportIdentityBackup(keystore);
  }

  /** 查看助记词（需口令）。返回后由调用方负责展示与及时清理。 */
  async revealMnemonic(password: string): Promise<string[]> {
    const keystore = await this.storage.getKeystore();
    if (!keystore) throw new MeshLinkError('E_IDENTITY_NOT_FOUND');
    const bundle = await openKeystore(keystore, password);
    return [...bundle.mnemonic];
  }

  /** 当前 keystore（密文，供 UI 展示「已加密存储」状态）。 */
  async keystoreSummary(): Promise<{ version: number; kdf: string; iterations: number } | null> {
    const keystore: EncryptedKeystore | null = await this.storage.getKeystore();
    if (!keystore) return null;
    return { version: keystore.version, kdf: keystore.kdf.name, iterations: keystore.kdf.iterations };
  }

  /** 注销：清库 + 清 keystore + 内存清零。 */
  async destroy(): Promise<void> {
    this.lock();
    await this.storage.clearAll();
    await this.storage.close();
    try {
      localStorage.removeItem(LS_KEYS.lastDid);
      localStorage.removeItem(LS_KEYS.onboarded);
    } catch {
      // 忽略
    }
    this.openedDid = null;
  }
}

export default IdentityService;
